<template>
  <Notification />
  <PlatformNotices />
  <div class="app__container" :class="containerClasses">
    <router-view name="Authorization" />
    <router-view name="UnauthorizedPanel" />
    <router-view name="Sidebar" />
    <MobileMenu />
    <div class="container-content" :class="containerContentClasses">
      <router-view />
    </div>
  </div>
</template>

<script>
import { watch, computed, onMounted } from 'vue';
import WalletService from '@/js/services/wallet.service';
import MobileMenu from '@/vue/components/MobileMenu/MobileMenu';
import Notification from '@/vue/components/common/notification/Notification';
import PlatformNotices from '@/vue/components/common/notification/PlatformNotices';
import { useRoute } from 'vue-router';
import { useRouter } from 'vue-router';
import { ref } from 'vue';
import { useStore } from 'vuex';
import api from '@/js/services/api';
import authRoutes from '@/router/authRoutes';
import { vueRoutes } from '@/router/router-const';
import {sendRequest} from "@/js/helpers/log-out-request";
import { takePendingPay } from '@/js/services/pay-link.service';
import {
  storeReferralCode,
  getStoredReferralCode,
  clearStoredReferralCode,
  recordReferralClick,
} from '@/js/services/affiliate.service';

export default {
  name: 'Main',
  components: { MobileMenu, Notification, PlatformNotices },
  setup() {
    const store = useStore();
    const router = useRouter();
    const isLoggedIn = computed(() => store.getters['auth/isLoggedIn']);
    const wallet = computed(() => store.getters['auth/getPrivateData']);
    const verificationStatus = ref(0);
    const route = useRoute();

    const setUserData = async () => {
      if (isLoggedIn.value) {
        await fetchVerificationStatus();
        await fetchAvatar();
        await fetchBalances();
      }
    };

    const isAuthorizationPage = computed(() => {
      return authRoutes.some((authorizationRoute) => {
        return route.path.includes(authorizationRoute.path);
      });
    });

      let intervalId = setInterval(async () => {
        if (!store.state.auth.user.id){
          return
        }
        const resp = await sendRequest(store.state.auth.user.id);
        if (resp==="role changed"){
          clearInterval(intervalId);
          intervalId = null;
          return false;
        }
        if (resp === 404) {
          clearInterval(intervalId);
            await store.dispatch('logout');
            location.reload();
        }
      }, 5000);
    const verifyKycStatus = async () => {
      await store.dispatch('roles/fetchRolesData', store.state.auth.user.id);
    };

    const containerClasses = computed(() => {
      return {
        // The start page runs under the guest header, its picture up to the top edge.
        'app__container--margin':
          !isLoggedIn.value && !isAuthorizationPage.value && route.name !== vueRoutes.about.name,
      };
    });

    const containerContentClasses = computed(() => {
      return {
        'container-content--w100':
          !isLoggedIn.value || isAuthorizationPage.value,
      };
    });

    const fetchBalances = async () => {
      if (wallet.value) {
        await store.dispatch(
          'balances/fetchAssetBalances',
          wallet.value.wallet
        );
      }
    };
    const fetchReBalances = async () => {
      await store.dispatch('balances/fetchReBalances')
    }

    const fetchVerificationStatus = async () => {
      try {
        const response = await api.get(
          `identity/v1/roles/${store.state.auth.user.id}`
        );
        verificationStatus.value = response.data.role;
        await store.dispatch(
          'auth/setVerificationStatus',
          verificationStatus.value
        );
      } catch (error) {
        console.error('Error fetching verification status:', error);
      }
    };

    const fetchAvatar = async () => {
      try {
        const resp = await api.get(
          'identity/v1/kyc/avatar/' + store.state.auth.user.id
        );
        await store.dispatch('images/addAvatar', resp.data.key);
      } catch (error) {
        // 404 just means this user hasn't uploaded an avatar yet - not an error.
        if (error?.response?.status !== 404) {
          console.error(error);
        }
      }
    };

    const handleAuthState = async () => {
      if (isLoggedIn.value) {
        try {
          const response = await api.get('keys/v1/keys');
          if (response.data.data) {
            const keyData = response.data.data[0];
            WalletService.setPrivateData(keyData);
            await verifyKycStatus();
            await store.dispatch('auth/setPrivateData', keyData);
            if (keyData.recovered === true) {
              await router.push(vueRoutes.recoveryPrivateData);
            }
            if (store.getters['roles/kycProceeded']) {
              await router.push(vueRoutes.marketplace);
            } else if (!store.getters['roles/kycProceeded']) {
              await router.push(vueRoutes.authKYC);
            }
          } else {
            // A logged-in user with no keys yet is definitely a fresh
            // identity in this browser - any leftover isKycPreceeded flag
            // from a previous account can't apply and would wrongly skip
            // the KYC step, so clear it before sending them to mnemonic.
            await store.dispatch('roles/updateKycProceeded', false);
            await router.push(vueRoutes.mnemonic);
          }
        } catch (error) {
          await store.dispatch('notification/addNotification', {
            type: 'warning',
            code: error.response.data.status_code,
            message: error.response.data.error_key,
          });
        }
      }
    };

    // The /r/{code} redirect lands here with ?ref={code} attached (see
    // affiliateh.Redirect on the backend). The click may happen while logged
    // out, so the code is kept in localStorage until a wallet is known, then
    // reported once via /affiliate/click so the referrer gets credited on
    // this user's future purchases.
    const captureReferralCode = () => {
      if (route.query.ref) {
        storeReferralCode(route.query.ref);
      }
    };
    const reportReferralClickIfPending = async () => {
      const code = getStoredReferralCode();
      if (!code || !wallet.value?.wallet) return;
      try {
        await recordReferralClick(code, wallet.value.wallet);
        clearStoredReferralCode();
      } catch (error) {
        // Keep the code around - a transient failure here shouldn't lose the
        // referral, it'll just retry on the next mount/login.
        console.error('Error recording referral click:', error);
      }
    };

    onMounted(async () => {
      captureReferralCode();
      await fetchReBalances();
      if (isLoggedIn.value) {
        await setUserData();
        await verifyKycStatus();
        await reportReferralClickIfPending();
      }
    });
    const handleAfterLogin = async () => {
      await handleAuthState();
      await setUserData();
      await verifyKycStatus();
      await reportReferralClickIfPending();
      // a guest opened a payment link and logged in: back to it
      if (isLoggedIn.value) {
        const pending = takePendingPay();
        if (pending) router.push(pending);
      }
    };
    watch(isLoggedIn, handleAfterLogin);
    watch(() => route.query.ref, captureReferralCode);

    return {
      isLoggedIn,
      verificationStatus,
      wallet,
      containerClasses,
      containerContentClasses,
    };
  },
};
</script>

<style lang="scss">
@import '@/scss/app.scss';
@import './scss/variables';
@import './scss/mixins';

// Guest layout of pages also open without a login (/rent, /dao): the same
// margins as the marketplace's own `marketplace-wrapper--unlogged`.
.page--unlogged {
  margin: 0 calc($sidebar-width / 2);
  width: auto;

  @include respond-to($x-medium) {
    margin: 0;
    position: fixed;
    z-index: 10;
    top: $unauthorized-panel-height;
    left: 0;
    overflow-y: auto;
    bottom: 0;
    width: 100%;
  }
}

#app {
  display: flex;
  align-items: stretch;
  overflow-x: hidden;
  flex: 1;
  min-height: 100vh;
}

.app__container {
  display: flex;
  align-items: stretch;
  overflow-x: hidden;
  flex: 1;

  &--margin {
    margin-top: 8rem;
  }
}

.container-content {
  position: relative;
  margin-left: $sidebar-width;
  width: calc(100% - $sidebar-width);
  @include respond-to($sidebar-hide-bp) {
    margin-left: 0;
    width: 100%;
  }
  &--w100 {
    margin: 0;
    padding: 0;
    width: 100%;
  }
}
</style>
